import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { ErrorStatusEnum } from '@app/common';
import { useRouter } from 'next/router';
import { SignUpForm } from '../../forms/sign-up-form';
import { ThirdPartyLogin } from './third-party-login/third-party-login';
import { SignUpUserMobxDto } from '../../../../models/mobx/dtos/auth/sign-up-user-mobx-dto';
import { useRootStore } from '../../../../providers/root-store-provider';
import { DialogAccountLinks } from './links/links';
import { BaseDialog } from '../base-dialog';
import { IconUser } from '../../../app/icons/user';
import { IconButton } from '../../../app/icon/button/icon-button';
import { CampaignPromoBanner } from '../../campaign-promo-banner/campaign-promo-banner';
import { SignupSelectedPlanBanner } from './signup-selected-plan-banner';

export interface SignUpDialogInterface {
    action?: string;
    status?: string;
}

export const SignUpDialog: React.FC<SignUpDialogInterface> = observer((props: SignUpDialogInterface) => {
    const { t } = useTranslation();
    const rootStore = useRootStore();
    const { alertStore } = rootStore;
    const { authDialogStore } = rootStore.authStore;
    const { showSignupPromo } = authDialogStore;
    const { status } = props;
    const router = useRouter();
    const interestedInSubscription = rootStore.cookieService.getInterestedInSubscription();

    useEffect(() => {
        if (status === ErrorStatusEnum.SignedUpDifferently) {
            alertStore.setErrorMessageByStatus(status as ErrorStatusEnum);
            authDialogStore.setLoginActive(true);
        }
    }, []);

    const onSignup = async (values: SignUpUserMobxDto) => {
        if (authDialogStore.agreedTerms) {
            if (authDialogStore.activeModal === 'partnerSignUp') {
                await rootStore.authStore.partnerSignUp(values, router.query.token as string);
            } else {
                await rootStore.authStore.signUp(values);
            }

            if (rootStore.authStore.registrationDone) {
                rootStore.cookieService.setSignupCloseRedirectUrl(null);
            }
        }
    };

    const closeSignUpDialog = () => {
        const closeRedirectUrl = rootStore.cookieService.getSignupCloseRedirectUrl();

        if (authDialogStore.activeModal === 'signUp' && closeRedirectUrl) {
            rootStore.cookieService.setSignupCloseRedirectUrl(null);
            window.location.assign(closeRedirectUrl);
            return;
        }

        authDialogStore.closeDialog();
    };

    return (
        <>
            <BaseDialog
                opened={authDialogStore.activeModal === 'signUp' || authDialogStore.activeModal === 'partnerSignUp'}
                close={closeSignUpDialog}
                icon={<IconButton icon={<IconUser />} variant={'primary'} />}
                title={
                    authDialogStore.authStore.registrationDone
                        ? t('dialogs.signUp.titleDone')
                        : t('dialogs.signUp.title')
                }
                content={
                    <>
                        {authDialogStore.authStore.registrationDone && <>{t('dialogs.signUp.warningMessage')}</>}

                        {!authDialogStore.authStore.registrationDone && (
                            <>
                                {showSignupPromo && <CampaignPromoBanner />}

                                {authDialogStore.activeModal === 'signUp' && interestedInSubscription ? (
                                    <SignupSelectedPlanBanner lookupKey={interestedInSubscription} />
                                ) : null}

                                <SignUpForm onSubmit={onSignup} loading={authDialogStore.loading} />

                                {authDialogStore.activeModal === 'signUp' ? (
                                    <>
                                        <DialogAccountLinks type={authDialogStore.activeModal} />
                                        <ThirdPartyLogin />
                                    </>
                                ) : null}
                            </>
                        )}
                    </>
                }
            />
        </>
    );
});
